import React from "react";
import cns from "classnames";
import AppBar, { AppBarProps } from "@material-ui/core/AppBar";
import MuiTabs, { TabsProps as MuiTabsProps } from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Paper, { PaperProps } from "@material-ui/core/Paper";

import { ThemeType } from "@theme";
import Body from "./typography/Body";
import locale from "@bonliva-crm/locale";
import WithPopover from "@components/WithPopover";
import { makeStyles } from "@material-ui/core/styles";

export type TabProps = {
  content: React.ReactNode | (() => React.ReactNode);
  disabled?: boolean;
  fitted?: boolean;
  label: React.ReactNode | string;
  popoverContent?: React.ReactElement<any>;
  value: string;
  hidden?: boolean;
};

type TabsRenderProps = {
  header: React.ReactNode;
  page: React.ReactNode;
};

export type TabsProps = {
  AppBarProps?: AppBarProps;
  beforeContent?: React.ReactNode;
  children?: (props: TabsRenderProps) => React.ReactNode;
  className?: string;
  ContentProps?: any;
  id?: string;
  minWidth?: boolean;
  onChange?: (event: React.ChangeEvent<{}>, value: any) => void;
  PaperProps?: Partial<PaperProps>;
  tabs: TabProps[];
  TabsProps?: Partial<MuiTabsProps>;
  value?: string;
  withHref?: boolean;
  withPadding?: boolean;
  key?: string;
};

export function Tabs(props: TabsProps) {
  const classes = useStyles({});
  const activeTab = props.value
    ? props.tabs.find((tab) => tab.value === props.value)
    : props.tabs[0];

  const { beforeContent, children, withPadding, minWidth = true } = props;

  const value = props.value ? props.value : props.tabs[0] ? props.tabs[0].value : false;

  const contentClassName = cns(classes.content, {
    [classes.contentPadded]: !(activeTab?.fitted || withPadding),
    [classes.withPadding]: withPadding
  });

  const content = activeTab ? (
    typeof activeTab.content === "function" ? (
      (activeTab.content as any)() // a weird ts typing bug here
    ) : (
      activeTab.content
    )
  ) : (
    <Body>{locale.Empty}</Body>
  );
  const appBar = (
    <AppBar position="static" color="secondary" {...props.AppBarProps}>
      <MuiTabs onChange={props.onChange} value={value} {...props.TabsProps}>
        {props.tabs.map(
          (tab) =>
            !tab.hidden && (
              <Tab
                key={tab.value}
                data-test-id={tab.value}
                data-test="Tab"
                disabled={tab.disabled}
                href={
                  props.withHref ? `${window.location.pathname}#${tab.value}` : undefined
                }
                label={
                  <WithPopover
                    in={Boolean(tab.popoverContent)}
                    popoverContent={tab.popoverContent}
                    PopoverProps={{
                      anchorOrigin: {
                        horizontal: "center",
                        vertical: "bottom"
                      },
                      transformOrigin: {
                        horizontal: "center",
                        vertical: "top"
                      }
                    }}
                  >
                    <span>{tab.label}</span>
                  </WithPopover>
                }
                style={{ pointerEvents: "auto", overflow: "visible" }}
                value={tab.value}
                className={minWidth ? null : classes.minWidth}
              />
            )
        )}
      </MuiTabs>
    </AppBar>
  );

  const page = (
    <div
      style={{ marginBottom: 16 }}
      className={contentClassName}
      {...props.ContentProps}
    >
      {content}
    </div>
  );

  const _children =
    typeof children === "function" ? (
      children({ header: appBar, page })
    ) : (
      <>
        {props.tabs.length > 1 ? appBar : null}
        {beforeContent && <div className={classes.beforeContent}>{beforeContent}</div>}
        {page}
      </>
    );

  return (
    <Paper className={props.className} id={props.id} {...props.PaperProps}>
      {_children}
    </Paper>
  );
}

const useStyles = makeStyles((theme: ThemeType) => ({
  content: {
    overflowX: "auto"
  },

  contentPadded: {
    "& > *": {
      padding: theme.spacing(2)
    }
  },

  withPadding: {
    overflowX: "auto",
    padding: theme.spacing(2)
  },

  beforeContent: {
    width: "100%"
  },

  minWidth: {
    minWidth: "auto"
  }
}));
